import React, { useState } from 'react';
import { Star, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { StarRating } from './StarRating';
import { reviewsApi } from '../lib/api';
import { toast } from 'sonner';

const ReviewModal = ({ 
  isOpen, 
  onClose, 
  parcelId, 
  revieweeName,
  revieweeRole,
  onSuccess 
}) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Veuillez sélectionner une note');
      return;
    }
    
    setLoading(true);
    try {
      await reviewsApi.create({
        parcel_id: parcelId,
        rating,
        comment,
      });
      toast.success('Merci pour votre avis!');
      onSuccess?.();
      onClose();
      setRating(0);
      setComment('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  const roleLabel = revieweeRole === 'carrier' ? 'le cotransporteur' : 'l\'expéditeur';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-900 uppercase">
            Laisser un avis
          </DialogTitle>
          <DialogDescription>
            Évaluez {roleLabel} <span className="font-semibold text-slate-700">{revieweeName}</span>
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Star Rating */}
          <div className="space-y-3">
            <Label className="text-slate-700">Votre note</Label>
            <div className="flex justify-center py-4 bg-slate-50 rounded-lg">
              <StarRating
                rating={rating}
                size="xl"
                interactive
                onChange={setRating}
              />
            </div>
            <div className="flex justify-center">
              {rating > 0 && (
                <span className="text-sm text-slate-600">
                  {rating === 1 && 'Très insatisfait'}
                  {rating === 2 && 'Insatisfait'}
                  {rating === 3 && 'Correct'}
                  {rating === 4 && 'Satisfait'}
                  {rating === 5 && 'Très satisfait'}
                </span>
              )}
            </div>
          </div>
          
          {/* Comment */}
          <div className="space-y-2">
            <Label htmlFor="comment" className="text-slate-700">
              Commentaire (optionnel)
            </Label>
            <Textarea
              id="comment"
              placeholder="Partagez votre expérience..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="min-h-[100px] resize-none"
              data-testid="review-comment"
            />
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-slate-200"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading || rating === 0}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="submit-review-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewModal;
